import React, { useMemo, useState } from "react";
import Th from "./Th";
import StatusBadge from "./StatusBadge";
import GroupedTripLink from "./GroupedTripLink";
import Pagination from "./Pagination";
import { formatDate } from "./helpers";
import { PAGE_SIZE } from "./constants";

const GroupedTripsTable = ({ groupedTrips }) => {
  const [sortKey, setSortKey] = useState("occurred_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    const copy = [...groupedTrips];
    copy.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return copy;
  }, [groupedTrips, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  // `page` is raw user state; clamp on render so a shrinking dataset doesn't
  // need an effect-driven reset. Pagination's prev/next handlers use this
  // clamped value, so navigation still feels right after the dataset shrinks.
  const safePage = Math.min(page, pageCount);
  const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return (
    <section className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-[12px] uppercase tracking-widest text-gray-500 font-semibold">
          Recent Trips Activity ({groupedTrips.length})
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="bg-gray-50/50 border-b border-gray-200">
            <tr>
              <Th onClick={() => onSort("occurred_at")} active={sortKey === "occurred_at"} dir={sortDir}>
                Date &amp; Time
              </Th>
              <Th>Grouped Trip ID</Th>
              <Th onClick={() => onSort("status")} active={sortKey === "status"} dir={sortDir}>
                Status
              </Th>
              <Th onClick={() => onSort("duration_min")} active={sortKey === "duration_min"} dir={sortDir} align="right">
                Duration
              </Th>
              <Th onClick={() => onSort("distance_km")} active={sortKey === "distance_km"} dir={sortDir} align="right">
                Distance
              </Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pageRows.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-6 text-center text-sm text-gray-500 italic">
                  No trips this week.
                </td>
              </tr>
            ) : (
              pageRows.map((groupedTrip) => (
                <tr key={groupedTrip.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-[14px] font-medium">{formatDate(groupedTrip.occurred_at)}</td>
                  <td className="px-4 py-3 text-[13px] font-mono">
                    <GroupedTripLink id={groupedTrip.id} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={groupedTrip.status} />
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-500 text-right">
                    {groupedTrip.duration_min} mins
                  </td>
                  <td className="px-4 py-3 text-[13px] text-gray-500 text-right">
                    {groupedTrip.distance_km?.toFixed(1)} km
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {pageCount > 1 && (
        <Pagination page={safePage} pageCount={pageCount} onChange={setPage} total={groupedTrips.length} />
      )}
    </section>
  );
};

export default GroupedTripsTable;
